import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react'
import { Modal, RangeSelector } from '../../util/ui'
import { Button, Input, DropDown, MultiSelect, ButtonGroup, Label } from '../../util/ui_components'
import { useDispatch, useSelector } from 'react-redux'
import { pythonEscape, fetchByJSON, GetDataFrameInfo, useCachedData, toUnicode } from '../../util/util'
import { actions as DataSetActions } from '../../reducer/dataset'
import { useTable, usePagination, useSortBy } from 'react-table'
import { InlineTip } from '../common/tip';
import Table from '../common/table'
import authHeader from '../../services/auth-header';
import './index.css'
import Help from '../common/help'
import Sandbox from '../common/sandbox'
import sanitizeJsonString from '../common/sanitize'
import { thebeConfigR } from '../../util/thebeConfigs'
import { usePageKernel, attachCells } from '../../util/thebeSession'

const NumericComparators = ['<', '=', '>']
const CategoricalComparators = ['=']
const QueryType = {
    Numerical: 1,
    Categorical: 2
}

// const reconnectErrorFunction = () => {
//   alert('Kernel is taking too much time. Please reduce the data size!')
// }

const reconnectErrorFunction = () => {
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    background: transparent;
    z-index: 999999;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  const spinner = document.createElement('div');
  spinner.style.cssText = `
    width: 48px;
    height: 48px;
    border: 5px solid rgba(63, 91, 203, 0.84);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  `;

  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
  `;

  document.head.appendChild(style);
  overlay.appendChild(spinner);
  document.body.appendChild(overlay);

  setTimeout(() => {
    overlay.remove();
    style.remove();
  }, 1000);
};

const getQString = (type, numQuery = { min: Number.MIN_SAFE_INTEGER, max: Number.MAX_SAFE_INTEGER }, cateQuery = [], searchColumn) => {
    if (type === QueryType.Numerical) {
        return JSON.stringify({
            min: Number(numQuery.min.toFixed(2)),
            max: Number(numQuery.max.toFixed(2)),
            column: numQuery.column
        });
    }

    if (type === QueryType.Categorical) {
        cateQuery.sort();
        return JSON.stringify({
            column: searchColumn,
            cates: cateQuery
        });
    }
}


const getCodeFromConditions = ({ conditions }) => {
    let queries = [];
    for (let condition of conditions) {
        let { queryType, qString } = condition;
        if (queryType == 2) {  // Categorical
            let { column, cates } = JSON.parse(qString);
            queries.push(`${column} %in% c(${cates.map(cate => `"${cate}"`).join(', ')})`);
        } else if (queryType == 1) {  // Numerical
            let { min, max, column } = JSON.parse(qString);

            if (min !== undefined) {
                queries.push(`${column} >= ${min}`);
            }

            if (max !== undefined) {
                queries.push(`${column} <= ${max}`);
            }
        }
    }
    console.log(conditions);
    let filterString = queries.length > 0 ? queries.join(' & ') : '';


    return `

if (!requireNamespace("dplyr", quietly = TRUE)) {
    install.packages("dplyr")
}

if (!requireNamespace("jsonlite", quietly = TRUE)) {
    install.packages("jsonlite")
}
suppressPackageStartupMessages({
  suppressWarnings(library(dplyr))
  suppressWarnings(library(jsonlite))
})
library(dplyr)
library(jsonlite)
# store query option(s)
filters <- fromJSON('${JSON.stringify(conditions).replace(/"/g, '\\"')}')

# query dataset based on selected option(s)
${filterString ? `df <- filter(df, ${filterString})` : ''}

# display dataset after querying
print(df)
`
}



const getInitialCode = data => `
library(jsonlite)
data_json <- '${data}'
# Convert JSON to a list. pandas' df.to_json() omits the row-index key entirely for any
# NaN cell (rather than including it as null), so columns with missing values end up with
# fewer entries than columns with none -- reindex every column against the union of all
# row indices first, so missing cells become proper NA in the right position, before
# combining into a data frame.
df_list <- fromJSON(data_json)
all_indices <- unique(unlist(lapply(df_list, names)))
all_indices <- all_indices[order(as.numeric(all_indices))]
columns_list <- lapply(df_list, function(col) {
    col_names <- names(col)
    sapply(all_indices, function(idx) {
        if (idx %in% col_names) {
            val <- col[[idx]]
            if (is.null(val)) NA else val
        } else {
            NA
        }
    })
})
names(columns_list) <- names(df_list)

# Combine the list into a proper data frame
df <- as.data.frame(columns_list, stringsAsFactors = FALSE, check.names = FALSE)
df <- type.convert(df, as.is = TRUE)
`

const supportCode = `
library(readr) # For reading CSV files
library(dplyr) # For data manipulation
library(ggplot2) # For data visualization

# Manually load other libraries if necessary

# Replace '<filename.csv>' with the dataset you need
df <- read_csv('<filename.csv>')
`

const Page = () => {
    useCachedData()

    const [sliderKey, setSliderKey] = useState(0);
    const [confirmedRanges, setConfirmedRanges] = useState({});
    const [forceFullRange, setForceFullRange] = useState(false);
    let dataset = useSelector(state => state.dataset)
    // this "currentFilter" hook only store the latest ONE applied filter
    let [currentFilter, setCurrentFilter] = useState([])
    let [stagedNumericalFilters, setStagedNumericalFilters] = useState([])
    let loadRef = useRef(true)
    let [searchColumn, setSearchColumn] = useState('Select a Column')
    let [inputOption, setInputOption] = useState('')
    let [selectedOptions, setSelectedOptions] = useState([])

    const [code, setCode] = useState('')
    const [activateStatus, setActivateStatus] = useState('Loading...')
    let codeParent = useRef()
    let kernelRef = useRef()
    // one kernel per mounted page, shut down again on unmount
    const getPageKernel = usePageKernel(thebeConfigR)
    let [dfJSON, setDfJSON] = useState('')//dataframe json

    let [queryType, setQueryType] = useState(0)

    let numericalRangeRef = useRef({})
    let categoricalRef = useRef([])
    let dispatch = useDispatch()
    let sandboxRef = useRef(null)


    useEffect(async () => {
        let res = await fetchByJSON('query', {
            filters: JSON.stringify(dataset.dataFilters),
            filename: dataset.filename,
            setSource: loadRef.current
        })

        loadRef.current = false

        let json = await res.json()
        let json_string = sanitizeJsonString(json.data, false);
        dispatch(DataSetActions.setTableData(JSON.parse(json_string)))
        setCode(getCodeFromConditions({ conditions: dataset.dataFilters }))
        console.log(currentFilter)
    }, [dataset.dataFilters])

    useEffect(() => {
        reconnectErrorFunction();
        if (!code) return
        codeParent.current.innerHTML = ''
        let pre = document.createElement('pre')
        pre.setAttribute('data-executable', 'true')
        pre.setAttribute('data-language', 'R')
        codeParent.current.appendChild(pre)
        pre.innerHTML = code
        let isActive = true

        // Only the freshly created <pre> needs rendering here -- bootstrap() would also
        // request another kernel, and thebe's "status" bus is global, so the handler this
        // used to register kept firing (for every page's kernel) after unmount.
        getPageKernel().then((kernel) => {
            if (!isActive) return
            kernelRef.current = kernel
            kernelRef.current._reconnect = reconnectErrorFunction;
            attachCells(kernel)
        }).catch((err) => console.error('Error connecting to the R kernel:', err))

        return () => { isActive = false }
    }, [code])

    //start thebelab automatically
    //load current dataframe
    useEffect(() => {
        reconnectErrorFunction();
        if (!dataset.filename) {
            setActivateStatus('No data')
            return
        }

        let isActive = true

        //excute code in advance on thebelab to import libraries and set dataframe variable
        getPageKernel().then(async (kernel) => {
            if (!isActive) return
            let res = await fetchByJSON('current_data_json', {
                filename: dataset.filename
            })

            let g = await res.json()
            if (!isActive) return
            kernelRef.current = kernel
            kernelRef.current._reconnect = reconnectErrorFunction;
            setDfJSON(g.data)
            setActivateStatus('Ready')
        }).catch((err) => {
            if (!isActive) return
            console.error('Error connecting to the R kernel:', err)
            setActivateStatus('Error connecting to R')
        })

        return () => { isActive = false }
    }, [dataset.filename])

    const runCode = async (e) => {
        try {
            setActivateStatus('Running Initial Code ...');
        let res = await fetchByJSON('current_data_json', {
            filename: dataset.filename
        })
        let json = await res.json();
        let json_string = sanitizeJsonString(json.data);
        let res2 = await kernelRef.current.requestExecute({ code: getInitialCode(json_string) }).done
        // console.log(res2);
        document.querySelector('.thebelab-run-button').click()
        setActivateStatus('Ready');
        } catch (err) {
            console.error("Error during run:", err);
            setActivateStatus('Error during run');
        }
    }

    // useEffect(() => {
    // }, [currentFilter])

    const setComparators = useCallback((name) => {
        if (dataset.num_cols.indexOf(name) !== -1) {
            setQueryType(QueryType.Numerical)
            // If there is a confirmed range for this column, use it; otherwise use dataset default
            numericalRangeRef.current = confirmedRanges[name] ? { ...confirmedRanges[name] } : {
                min: dataset.num_lists[name].min,
                max: dataset.num_lists[name].max,
                column: name
            }
            // force a re-render of the RangeSelector component
            setSliderKey(prev => prev + 1)
        } else if (dataset.cate_cols.indexOf(name) !== -1) {
            setQueryType(QueryType.Categorical)
        }
    }, [dataset, confirmedRanges])

    const addFilter = () => {
        if (queryType === 0) return;

        if (queryType === QueryType.Numerical) {
            // Prepare the new filter for the current range
            let qString = getQString(queryType, numericalRangeRef.current, categoricalRef.current, searchColumn);
            let newFilter = {
                queryType,
                qString,
                desc: `Range of ${searchColumn}: [${numericalRangeRef.current.min}, ${numericalRangeRef.current.max}]`
            };
            setStagedNumericalFilters(prev => {
                const exists = prev.some(f => f.qString === newFilter.qString);
                if (!exists) return [...prev, newFilter];
                return prev;
            });
            setConfirmedRanges(prev => ({
                ...prev,
                [searchColumn]: { ...numericalRangeRef.current }
            }));
            setCurrentFilter([newFilter]);
            return;
        }
        const newFilter = currentFilter[0];
        let updatedFilters = dataset.dataFilters.filter(filter => {
            return !filter.desc.includes(`of ${searchColumn}`);
        });
        updatedFilters.push(newFilter);
        dispatch(DataSetActions.setFilters(updatedFilters));
    }

    const confirmNumericalFilters = () => {
        let updatedFilters = dataset.dataFilters.filter((f) => {
            if (!f || typeof f !== 'object') return false;
            if (f.queryType === QueryType.Numerical) {
                const col = JSON.parse(f.qString).column;
                return !stagedNumericalFilters.some(sf => JSON.parse(sf.qString).column === col);
            }
            return true;
        });
        updatedFilters = [...updatedFilters, ...stagedNumericalFilters];
        dispatch(DataSetActions.setFilters(updatedFilters));
        setStagedNumericalFilters([]);
        setCurrentFilter([]);
    }

    const removeStagedFilter = (idx) => {
        setStagedNumericalFilters(prev => {
            const removed = prev[idx];
            const next = prev.filter((_, i) => i !== idx);
            setCurrentFilter(cf => {
                try {
                    if (cf && cf[0] && removed && cf[0].qString === removed.qString) return [];
                } catch (e) {}
                return cf;
            });
            return next;
        });
    }

    const onUndo = async (e) => {
        let res = await fetchByJSON('cleanEditedCache', {
            filename: dataset.filename
        })
        let json;
        try {
            json = await res.json()
        } catch (err) {
            const txt = await res.text().catch(() => '')
            json = { success: res.ok }
        }

        if (json && json.success) {
            let previous = dataset.dataFilters.slice(0, -1)
            let removed = dataset.dataFilters.length ? dataset.dataFilters[dataset.dataFilters.length - 1] : null
            let removedColumn = null
            try {
                if (removed && removed.queryType === QueryType.Numerical) {
                    const parsed = JSON.parse(removed.qString)
                    removedColumn = parsed.column
                }
            } catch (err) {
                removedColumn = null
            }

            dispatch(DataSetActions.setFilters(previous))
            setCurrentFilter([])
            setCode(getCodeFromConditions({ conditions: previous }))

            if (removedColumn) {
                setConfirmedRanges(prev => {
                    const next = { ...prev }
                    delete next[removedColumn]
                    return next
                })
            }

            if (removedColumn && removedColumn === searchColumn) {
                setSliderKey(prev => prev + 1)
                setForceFullRange(true)
                setTimeout(() => setForceFullRange(false), 100)
            }
        }
    }

    const onRevert = async (e) => {
        if (dataset.filename) {
            let res = await fetchByJSON('cleanEditedCache', {
                filename: dataset.filename
            })
            let json;
            try {
                json = await res.json()
            } catch (err) {
                const txt = await res.text().catch(() => '')
                json = { success: res.ok }
            }

            if (json && json.success) {
                alert('Revert data success!')
                dispatch(DataSetActions.emptyInfo())

                // fetch fresh file info/data
                let res2 = await fetch('/file/?filename=' + dataset.filename + '&default=' + (dataset.isDefault === true), {
                    method: 'GET',
                    headers: authHeader()
                })

                let json2;
                try {
                    json2 = await res2.json()
                } catch (err) {
                    await res2.text().catch(() => '')
                    json2 = { success: res2.ok }
                }

                setConfirmedRanges({});
                setForceFullRange(true);
                setSliderKey(prev => prev + 1);
                setCurrentFilter([]);
                setTimeout(() => setForceFullRange(false), 100);
                if (json2 && json2.success) {
                    dispatch(DataSetActions.setData({
                        filename: dataset.filename,
                        info: GetDataFrameInfo(json2.info),
                        data: JSON.parse(json2.data),
                        cols: json2.cols,
                        num_cols: json2.num_cols,
                        col_lists: json2.col_lists,
                        cate_cols: json2.cate_cols,
                        cate_lists: json2.cate_lists,
                        num_lists: json2.num_lists
                    }));
                }
            }
        }
    }

    const onDownload = () => {
        const element = document.createElement('a')
        const file = new Blob([supportCode, code], { type: "text/plain" })
        element.href = URL.createObjectURL(file)
        element.download = "download_test.r" // or .txt
        document.body.appendChild(element)
        element.click()
    }

    function hashCode(string) {
        var hash = 0, i, chr;
        if (string.length === 0) return hash;
        for (i = 0; i < string.length; i++) {
            chr = string.charCodeAt(i);
            hash = ((hash << 5) - hash) + chr;
            hash |= 0; // Convert to 32bit integer
        }
        return hash;
    }

    let globalMin = dataset.num_lists[searchColumn] ? dataset.num_lists[searchColumn].min : 0;
    let globalMax = dataset.num_lists[searchColumn] ? dataset.num_lists[searchColumn].max : 100;
    let confirmed = confirmedRanges[searchColumn]
    let initialLeftValue = forceFullRange ? globalMin : (confirmed ? confirmed.min : globalMin)
    let initialRightValue = forceFullRange ? globalMax : (confirmed ? confirmed.max : globalMax)

    return (<>
        {/* <Sandbox ref={sandboxRef} dataset={dataset} additional={`import json`}/> */}
        <div className='flex flex-col bg-gray-100' >
            <div className="flex flex-row h-20 w-full items-center justify-between bg-gray-100 shadow-md">

                <div className='mx-5'>
                    <DropDown width={'w-48'} defaultText={searchColumn} zIndex={100} items={dataset.data ? Object.keys(dataset.data).map(name => ({
                        name,
                        onClick(e) {
                            setSearchColumn(name)
                            setComparators(name)
                            return false
                        }
                    })) : []} />
                </div>

                <div className='text-center'>
                    {queryType === QueryType.Numerical &&
                        globalMax !== undefined && globalMin !== undefined ?
                        <div className='w-auto gap-8 flex flex-col justify-center items-center px-1'>
                            <div className='flex flex-row gap-2 items-center w-full'>
                                {/* Staged filters on the left */}
                                {stagedNumericalFilters.length > 0 && (
                                    <div className="flex flex-row flex-nowrap gap-2 items-center justify-start overflow-x-auto" style={{minHeight: '2.5rem'}}>
                                        {stagedNumericalFilters.map((f, idx) => {
                                            const col = JSON.parse(f.qString).column;
                                            const min = JSON.parse(f.qString).min;
                                            const max = JSON.parse(f.qString).max;
                                            return (
                                                <span key={idx} className='px-1 py-0.5 bg-blue-100 rounded text-xs text-blue-800 border border-blue-300 whitespace-nowrap flex items-center gap-1'>
                                                    <span className="truncate">{col}: [{min}, {max}]</span>
                                                    <button
                                                        type="button"
                                                        aria-label="Remove staged filter"
                                                        onClick={() => removeStagedFilter(idx)}
                                                        className="ml-0.5 px-1 py-0.5 text-blue-600 hover:text-red-600"
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}
                                {/* RangeSelector and buttons */}
                                <div className='flex flex-row gap-2 items-center ml-2'>
                                    <RangeSelector
                                        key={sliderKey}
                                        resetFlag={forceFullRange ? { id: sliderKey, resetToFullRange: true } : sliderKey}
                                        resetToFullRange={forceFullRange}
                                        min={globalMin}
                                        max={globalMax}
                                        initialLeft={initialLeftValue}
                                        initialRight={initialRightValue}
                                        onEnd={(leftValue, rightValue) => {
                                            Object.assign(numericalRangeRef.current, { min: leftValue, max: rightValue })
                                            let qString = getQString(queryType, numericalRangeRef.current, categoricalRef.current, searchColumn)
                                            setCurrentFilter([{
                                                queryType,
                                                qString,
                                                desc: `Range of ${searchColumn}: [${leftValue}, ${rightValue}]`
                                            }])
                                        }} />
                                    <Button hasPadding={false} text="Add" overrideClass={`mx-1 px-1 rounded border focus:outline-none h-10`} onClick={addFilter} />
                                    <Button hasPadding={false} text="Clear" overrideClass={`mx-1 px-1 rounded border focus:outline-none h-10`} onClick={() => {
                                        setStagedNumericalFilters([]);
                                        setCurrentFilter([]);
                                    }} />
                                    <InlineTip info='Drag the sliding bars to change query window or double click them to manually input query value. Click Add to stack multiple filters.' />
                                </div>
                            </div>
                        </div>
                        : queryType === QueryType.Categorical ?
                            <div className='w-auto flex justify-start items-center gap-1'>
                                <div className='w-1/3'>
                                    <MultiSelect id="multi_select" passiveMode={true} selections={selectedOptions} onSelect={(e) => {
                                        categoricalRef.current = selectedOptions
                                    }} />
                                    {/* original multiselect query for string type: */}
                                    {/* <MultiSelect id="multi_select" width={'w-72'} selections={dataset.cate_lists[searchColumn] ? dataset.cate_lists[searchColumn] : []} onSelect={(e) => {
                                        categoricalRef.current = e
                                    }} /> */}
                                </div>
                                <div className='w-1/3'>
                                    <Input id="input_option" attrs={{ list: "search_column_options" }} placeholder="Query item(s)" onInput={(e, v) => { setInputOption(v) }} />
                                    <datalist id="search_column_options">{dataset.cate_lists[searchColumn].map((item) => <option key={hashCode(item)} value={item} />)}</datalist>
                                </div>
                                <Button hasPadding={false} disabled={dataset.cate_lists[searchColumn].indexOf(inputOption) === -1} text="Add" overrideClass={
                                    `mx-1 px-1 rounded border focus:outline-none h-10 ${dataset.cate_lists[searchColumn].indexOf(inputOption) === -1 ? 'text-gray-300 cursor-default' : ''}`} onClick={(e) => {
                                        setSelectedOptions([...selectedOptions, inputOption])
                                        categoricalRef.current.push(inputOption)
                                        let qString = getQString(queryType, numericalRangeRef.current, categoricalRef.current, searchColumn)
                                        setCurrentFilter([{
                                            queryType,
                                            qString,
                                            desc: `Categories of ${searchColumn}`
                                        }])
                                    }} />
                                <Button hasPadding={false} text="Clear" overrideClass={`mx-1 px-1 rounded border focus:outline-none h-10`} onClick={(e) => {
                                    setSelectedOptions([])
                                    categoricalRef.current = []
                                }} />
                                <InlineTip info='Type or select value from dropdown options.' />
                            </div>
                            : ''}
                </div>

                {/* <div className='mx-5 w-3/12'>
                    <MultiSelect width="w-72" defaultText={`Applied filters`} allowDelete={false} passiveMode={true} selections={dataset.dataFilters} getDesc={e => e.desc} />
                </div> */}

                <div className='w-auto flex justify-center items-center px-1'>
                    <div className={``}>{activateStatus}</div>
                    <InlineTip zIndex={10} info='The loading status of a remote environment, R code will be executed in that environment as soon as it is ready.' />
                </div>
            </div>

            <div className="flex flex-row h-20 w-full items-center justify-between bg-gray-100 shadow-md">
                <div className='w-auto flex flex-row items-center gap-8 mx-5'>
                    <ButtonGroup buttons={[
                        queryType === QueryType.Numerical ? {
                            text: 'Confirm',
                            onClick: confirmNumericalFilters,
                            overrideClass: 'w-32 px-4 py-1 rounded font-semibold border focus:outline-none text-black',
                        } : {
                            text: 'Confirm',
                            onClick: addFilter,
                        },
                    // {
                    //     text:' Show code',
                    //     onClick: ()=>{
                    //         sandboxRef.current.setCode(getCodeFromConditions({conditions:dataset.dataFilters}))
                    //         sandboxRef.current.show()
                    //     }
                    // },
                    {
                        text: 'Run',
                        onClick: runCode,
                        overrideClass: `ml-5 w-32 px-4 py-1 rounded font-semibold border focus:outline-none text-black ${!code ? 'text-gray-400 cursor-default' : 'text-black cursor-pointer'}`,
                        customStyle: { backgroundColor: !!code ? '#4bd699' : 'inherit' }
                    },
                    {
                        text: 'Undo',
                        onClick: onUndo,
                    },
                    {
                        text: 'Revert',
                        onClick: onRevert
                    },
                    {
                        text: 'Download',
                        onClick: onDownload
                    }
                    ]} />
                </div>

            </div>
            <div className="w-full flex flex-nowrap">
                <div className='w-1/2 text-gray-500 font-semibold'>
                    <div className='scroll w-full flex justify-center items-center' style={{ height: '100%' }}>

                        <Label text="Results:">
                            <div id="display_results" style={{ whiteSpace: 'pre-wrap' }} >Select an operation to see preprocessed results</div>
                        </Label>
                        <Label text="">
                            <img id="img" src="" />
                        </Label>
                    </div>
                </div>
                <div className='flex-grow-1 w-1/2' ref={codeParent}>
                    {code ? code : <div className='w-full flex-grow-0 h-48 flex justify-center items-center text-gray-500 font-semibold'>
                        Select a filter to see the corresponding code
                    </div>}
                </div>
            </div>
            <Table PageSize={10} />
            {/* <Help url={"menu/data_querying"}/> */}
        </div>
    </>)
}

export default Page